import React from 'react';

interface Props {
  /** Short status under the spinner, e.g. "Searching ClinicalTrials.gov…" */
  message?: string;
  /** Optional secondary line */
  detail?: string;
  /** How many skeleton cards to show (default 5) */
  cards?: number;
  className?: string;
}

/**
 * Full-panel loading state for Trial Tracker: shimmering trial-card skeletons
 * plus a soft spinner and status copy. Used for initial load, search, and
 * followed-trials fetches.
 */
const TrackerLoadingPanel: React.FC<Props> = ({
  message = 'Loading trials…',
  detail,
  cards = 5,
  className = '',
}) => {
  return (
    <div className={`flex-1 flex flex-col min-h-0 animate-fadeIn ${className}`}>
      <div className="flex-none flex flex-col items-center justify-center gap-2.5 pt-10 pb-6 px-6">
        <div className="relative h-10 w-10">
          <div className="absolute inset-0 rounded-full border-2 border-tracker-line" />
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-tracker-accent border-r-tracker-accent/40 animate-spin" />
          <div className="absolute inset-[7px] rounded-full bg-tracker-statusBg animate-pulse" />
        </div>
        <div className="text-sm font-semibold text-tracker-ink text-center">{message}</div>
        {detail ? (
          <div className="text-[11.5px] text-tracker-muted text-center max-w-sm leading-snug">{detail}</div>
        ) : null}
      </div>

      <div className="flex-1 overflow-hidden px-5 pb-5 space-y-2.5" aria-hidden="true">
        {Array.from({ length: cards }).map((_, i) => (
          <div
            key={i}
            className="tracker-skel-card border border-tracker-line rounded-xl px-4 py-3.5 bg-white"
            style={{ animationDelay: `${i * 90}ms` }}
          >
            <div className="flex items-center gap-2 mb-2.5">
              <div className="tracker-skel-bar h-4 w-14 rounded-full" />
              <div className="tracker-skel-bar h-4 w-20 rounded-full" />
              <div className="tracker-skel-bar h-4 w-10 rounded-full ml-auto" />
            </div>
            <div className="tracker-skel-bar h-3.5 w-[78%] rounded mb-2" />
            <div className="tracker-skel-bar h-3 w-[52%] rounded mb-3" />
            <div className="flex items-center gap-2">
              <div className="tracker-skel-bar h-2.5 w-24 rounded" />
              <div className="tracker-skel-bar h-2.5 w-16 rounded" />
              <div className="tracker-skel-bar h-2.5 w-20 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrackerLoadingPanel;
